import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Data, Params, UrlSegment } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { BreadcrumbObject } from '@shared/store/router/reducers/router-breadcrumbs.reducers';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  public breadcrumbTranslations = {
    'Home': 'BreadcrumbComponent:Home',
    'Search': 'BreadcrumbComponent:Search',
    'Booking Successful': 'BreadcrumbComponent:BookingReservationSuccessful',
    'Booking Reservation Successful': 'BreadcrumbComponent:BookingReservationSuccessful',
    'Booking Confirmation': 'BreadcrumbComponent:BookingConfirmation',
    'Booking Summary': 'BreadcrumbComponent:Booking',
    'Travel': 'BreadcrumbComponent:Travel',
    'Dining': 'BreadcrumbComponent:Dining',
    'Entertainment': 'BreadcrumbComponent:Entertainment',
    'Profile': 'BreadcrumbComponent:Profile',
    'Member Events': 'BreadcrumbComponent:MemberEvents',
    'Member event': 'BreadcrumbComponent:MemberEvents',
    'Event': 'BreadcrumbComponent:Event',
    'Offer': 'BreadcrumbComponent:Offer',
    'Offers': 'BreadcrumbComponent:Offers',
    'Summary': 'BreadcrumbComponent:Summary',
    'Tag': 'BreadcrumbComponent:Tag',
    'Brand': 'BreadcrumbComponent:Brand',
    'Inspiration': 'BreadcrumbComponent:Inspiration',
    'Publication': 'BreadcrumbComponent:Publication',
    'Articles': 'BreadcrumbComponent:Articles',
    'Article': 'BreadcrumbComponent:Article',
    'Details': 'BreadcrumbComponent:Details',
    'Preferences': 'BreadcrumbComponent:Preferences',
    'Activities': 'BreadcrumbComponent:Activities',
    'Briefing': 'BreadcrumbComponent:Briefing',
    'Car Results': 'BreadcrumbComponent:CarResults',
    'Car Details': 'BreadcrumbComponent:CarDetails',
    'Flight Results': 'BreadcrumbComponent:FlightResults',
    'Flight Details': 'BreadcrumbComponent:FlightDetails',
    'Flight Summary': 'BreadcrumbComponent:FlightSummary',
    'Hotel Results': 'BreadcrumbComponent:HotelResults',
    'Hotel Details': 'BreadcrumbComponent:HotelDetails',
    'Hotel Rooms': 'BreadcrumbComponent:HotelRooms',
    'Restaurant': 'BreadcrumbComponent:Restaurant',
    'Request Summary': 'BreadcrumbComponent:RequestSummary',
    'Partners': 'BreadcrumbComponent:Partners',
    'Options': 'BreadcrumbComponent:Options',
    'Traveller details': 'BreadcrumbComponent:TravellerDetails',
    'Dining Search': 'BreadcrumbComponent:DiningSearch',
    'Entertainment search': 'BreadcrumbComponent:EntertainmentSearch',
    'Offers search': 'BreadcrumbComponent:OffersSearch',
    'Member events search': 'BreadcrumbComponent:MemberEventsSearch',
    'Accessibility': 'FooterComponent:FooterAccessibility',
    'Privacy statement': 'FooterComponent:FooterPrivacy',
    'Terms & conditions': 'FooterComponent:FooterTerms',
    'Faq': 'FooterComponent:FooterFaq',
    'Contact': 'FooterComponent:FooterContact',
    'Error': 'BreadcrumbComponent:Error',
    'YourPreferences': 'BreadcrumbComponent:YourPreferences',
    'Centurion Benefits': 'BreadcrumbComponent:Benefits',
    'CardArts': 'BreadcrumbComponent:CardArts',
    'Offers & benefits': 'BreadcrumbComponent:BenefitsOffers',
    'Flights': 'BreadcrumbComponent:FlightsLanding',
    'KehindeWileyCardArt': 'BreadcrumbComponent:KehindeWiley',
    'RemKoolhaasCardArt': 'BreadcrumbComponent:RemKoolhaas',
    'ClassicBlackCardArt': 'BreadcrumbComponent:ClassicBlack',
    'PrivateJets': 'BreadcrumbComponent:PrivateJets',
    'HotelsLodging': 'BreadcrumbComponent:HotelsLodging',
    'CruisesAndTours': 'BreadcrumbComponent:CruisesAndTours',
    'Cruises': 'BreadcrumbComponent:Cruises',
    'Trains': 'BreadcrumbComponent:Trains',
    'Tours': 'BreadcrumbComponent:Tours',
    'Cars': 'BreadcrumbComponent:Cars',
    'Travelling': 'BreadcrumbComponent:Travelling',
    'AirportLounge': 'BreadcrumbComponent:AirportLounge',
    'HotelLoyalty': 'BreadcrumbComponent:HotelLoyalty',
    'Airlines': 'BreadcrumbComponent:Airlines',
    'Rewards': 'BreadcrumbComponent:Rewards',
    'RewardsPoints': 'BreadcrumbComponent:RewardsPoints',
    'RewardsRegistration': 'BreadcrumbComponent:RewardsRegistration',
    'InsuranceAssistance': 'BreadcrumbComponent:InsuranceAssistance',
    'TravelInsurance': 'BreadcrumbComponent:TravelInsurance',
    'ShoppingInsurance': 'BreadcrumbComponent:ShoppingInsurance',
    'Service': 'BreadcrumbComponent:Service',
    'Registration': 'BreadcrumbComponent:Registration',
    'Lifestyle': 'BreadcrumbComponent:BenefitsLifestyle',
    'LifestyleEvents': 'BreadcrumbComponent:BenefitsEvents',
    'LifestyleDining': 'BreadcrumbComponent:BenefitsDining',
    'SixtRide': 'BreadcrumbComponent:SixtRide',
    'CovidTravel': 'BreadcrumbComponent:PrepareForTravel',
    'Activities Results': 'BreadcrumbComponent:TravelActivitiesResults',
    'Activity details': 'BreadcrumbComponent:TravelActivityDetails',
    'Bookings': 'BreadcrumbComponent:Bookings',
    'Results': 'Entertainment:Resultsbreadcrumb',
    'Details Page': 'Entertainment:EventDetailsBreadcrumb',
    'Request Management': 'RequestManagement:ModuleTitle',
    'Purchase Package': 'BreadcrumbComponent:PurchaseRequest',
    'Checkout': 'BreadcrumbComponent:RequestPurchaseCheckout',
    'Package Request': 'BreadcrumbComponent:RequestSummary',
    'Package Request Confirmation': 'BreadcrumbComponent:RequestConfirmation',
    'Payment Successful': 'BreadcrumbComponent:PackagePaymentConfirmation',
  };

  public readonly HOME_TITLE = 'Home';

  public customVariables$: Observable<CustomisableVariables> = this.customVariables.variables;

  constructor(
    private titleService: Title,
    private translateService: TranslateService,
    private siteSettingsService: SiteSettingsService,
    private customVariables: CustomisableVariablesService
  ) {
    this.translateService.onLangChange.subscribe(() => undefined);
    this.translateService.onDefaultLangChange.subscribe(() => undefined);
  }

  /**
   * Generate the 'breadcrumbs' array based off information stored within the 'data' attribute of each route.
   * Called each time the application route changes
   * @param route - The application's current route.
   */
  public getBreadcrumbs(route: ActivatedRoute): Array<BreadcrumbObject> {
    const breadcrumbs: Array<BreadcrumbObject> = [
      {
        title: this.HOME_TITLE,
        url: '',
      },
    ];
    while (route) {
      const url = (route.url as BehaviorSubject<UrlSegment[]>).getValue();
      const data = (route.data as BehaviorSubject<Data>).getValue();
      const title = data.breadcrumb;
      const path = url.length ? data.breadcrumbRoute || url[0].path : null;
      let customVariablesBreadcrumbs = {};

      if (data.customVariableBreadcrumb) {
        this.customVariables$.pipe(take(1)).subscribe((variables) => {
          customVariablesBreadcrumbs = {
            Partners: variables.navigation_partners_link_text,
          };
        });
      }

      if (title && path && !this.isDuplicated(title, breadcrumbs)) {
        const parentPath = this.getLastPath(breadcrumbs);

        breadcrumbs.push({
          title,
          customVariableTitle: customVariablesBreadcrumbs[title],
          url: `${parentPath}/${path}`,
          params: (route.params as BehaviorSubject<Params>).getValue(),
          localSearchKey: data.localSearchKey,
        });
      }

      route = route.children[0];
    }

    this.setTitle(breadcrumbs);
    return breadcrumbs;
  }

  /**
   * Loops through the breadcrumbs array and returns true if the passed breadcrumb title has previously
   * been pushed into the breadcrumbs array. Prevents duplications within the 'breadcrumbs' array
   * @param title - The title of the breadcrumb to check
   * @param breadcrumbs - The list of already selected breadcrumbs
   */
  public isDuplicated(title: string, breadcrumbs: Array<BreadcrumbObject>): boolean {
    return breadcrumbs.some((breadcrumb) => breadcrumb.title === title);
  }

  public getLastPath(breadcrumbs: Array<BreadcrumbObject>): string {
    return breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1].url : '';
  }

  /**
   Putting this.translateService.get in combineLatest is to make sure the translations are already fetched from BE.
   */
  public setTitle(breadcrumbs: Array<BreadcrumbObject>) {
    combineLatest([
      this.siteSettingsService.fetchSiteSettings(),
      this.translateService.get(this.breadcrumbTranslations.Home),
    ])
      .pipe(take(1))
      .subscribe(([settings]: [ISiteSettings, string]) => {
        if (!breadcrumbs.length) {
          return;
        }

        let title = '';
        const titlePrefix = this.getTitlePrefix(breadcrumbs);
        const prefix = titlePrefix ? `${titlePrefix} - ` : '';
        const clientName = settings.fe_configuration.name;

        title = `${prefix}${clientName}`;
        this.titleService.setTitle(title);
      });
  }

  /**
   * Creates the title for the browser tab, gets the translated breadcrumbs to create it
   */
  public getTitlePrefix(breadcrumbs: Array<BreadcrumbObject>): string {
    return breadcrumbs
      .slice(breadcrumbs.length > 1 ? 1 : 0)
      .map((result) => this.translateService.instant(this.breadcrumbTranslations[result.title]))
      .join(' • ');
  }
}
